import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { CloudUpload, Visibility, Delete } from '@mui/icons-material';
import apiService from '../services/apiService';
import config from '../config/config';

const DriverDocumentsPage = () => {
  const [drivers, setDrivers] = useState([]);
  const [selectedDriver, setSelectedDriver] = useState('');
  const [driverDetails, setDriverDetails] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [selectedDocType, setSelectedDocType] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [viewImageUrl, setViewImageUrl] = useState('');
  const [openImageViewer, setOpenImageViewer] = useState(false);
  const [deleteDocumentId, setDeleteDocumentId] = useState(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);

  useEffect(() => {
    fetchDrivers();
    fetchDocumentTypes();
  }, []);

  const fetchDrivers = async () => {
    try {
      const response = await apiService.getDrivers({});
      if (response.success) {
        setDrivers(response.data || []);
      }
    } catch (err) {
      console.error('Error fetching drivers:', err);
    }
  };

  const fetchDocumentTypes = async () => {
    try {
      const response = await apiService.getDocumentTypes();
      if (response.success) {
        setDocumentTypes(response.data || []);
      }
    } catch (err) {
      console.error('Error fetching document types:', err);
    }
  };

  const handleDriverSelect = async (driverId) => {
    setSelectedDriver(driverId);
    setError('');
    setSuccess('');

    if (!driverId) {
      setDriverDetails(null);
      setDocuments([]);
      return;
    }

    try {
      setLoading(true);
      const response = await apiService.getDriverDetails(driverId);
      if (response.success) {
        setDriverDetails(response.data);
        setDocuments(response.data.documents || []);
      }
    } catch (err) {
      setError('Failed to load driver details');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError('File size should not exceed 5MB');
        return;
      }
      setSelectedFile(file);
      setError('');
    }
  };

  const handleUploadDocument = async () => {
    if (!selectedDriver) {
      setError('Please select a driver first');
      return;
    }

    if (!selectedDocType) {
      setError('Please select document type');
      return;
    }

    if (!selectedFile) {
      setError('Please select a file to upload');
      return;
    }

    try {
      setLoading(true);
      setError('');
      setSuccess('');

      const formData = new FormData();
      formData.append('documentType', selectedDocType);
      formData.append('document', selectedFile);

      const response = await apiService.uploadDriverDocument(selectedDriver, formData);

      if (response.success) {
        setSuccess('Document uploaded successfully!');
        setSelectedFile(null);
        setSelectedDocType('');
        // Refresh driver details to show new document
        handleDriverSelect(selectedDriver);
      } else {
        setError(response.message || 'Failed to upload document');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to upload document');
    } finally {
      setLoading(false);
    }
  };


  const handleViewDocument = (documentUrl) => {
    // Remove leading slash if present to avoid double slashes
    const cleanUrl = documentUrl.startsWith('/') ? documentUrl.substring(1) : documentUrl;
    const fullUrl = `${config.SOCKET_URL}/${cleanUrl}`;
    console.log('Document URL:', documentUrl);
    console.log('Clean URL:', cleanUrl);
    console.log('Full URL:', fullUrl);
    setViewImageUrl(fullUrl);
    setOpenImageViewer(true);
  };

  const handleCloseImageViewer = () => {
    setOpenImageViewer(false);
    setViewImageUrl('');
  };

  const handleOpenDeleteDialog = (documentId) => {
    setDeleteDocumentId(documentId);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setDeleteDocumentId(null);
  };

  const handleDeleteDocument = async () => {
    if (!deleteDocumentId) return;

    try {
      setLoading(true);
      setError('');
      setSuccess('');

      const response = await apiService.deleteDriverDocument(selectedDriver, deleteDocumentId);

      if (response.success) {
        setSuccess('Document deleted successfully!');
        handleCloseDeleteDialog();
        handleDriverSelect(selectedDriver); // Refresh documents list
      } else {
        setError(response.message || 'Failed to delete document');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete document');
    } finally {
      setLoading(false);
    }
  };

  const getStatusChip = (status) => {
    const statusConfig = {
      pending: { label: 'Pending', color: 'warning' },
      verified: { label: 'Verified', color: 'success' },
      rejected: { label: 'Rejected', color: 'error' },
    };
    const config = statusConfig[status] || statusConfig.pending;
    return <Chip label={config.label} color={config.color} size="small" />;
  };

  return (
    <Box>
      <Typography variant="h4" fontWeight="bold" gutterBottom>
        Manage Driver Documents
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Select a driver and upload their verification documents
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Driver Selection Card */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Select Driver
              </Typography>

              <FormControl fullWidth sx={{ mt: 2 }}>
                <InputLabel>Driver</InputLabel>
                <Select
                  value={selectedDriver}
                  onChange={(e) => handleDriverSelect(e.target.value)}
                  label="Driver"
                >
                  <MenuItem value="">
                    <em>Select a driver</em>
                  </MenuItem>
                  {drivers.map((driver) => (
                    <MenuItem key={driver.id} value={driver.id}>
                      {driver.name} - {driver.phone}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {driverDetails && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Driver Details
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    <strong>Name:</strong> {driverDetails.name}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Phone:</strong> {driverDetails.phone}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Status:</strong>{' '}
                    <Chip label={driverDetails.status} size="small" />
                  </Typography>
                  <Typography variant="body2">
                    <strong>Verification:</strong>{' '}
                    {getStatusChip(driverDetails.verification_status)}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>

          {/* Upload Document Card */}
          {selectedDriver && (
            <Card sx={{ mt: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Upload Document
                </Typography>

                <FormControl fullWidth sx={{ mt: 2 }}>
                  <InputLabel>Document Type</InputLabel>
                  <Select
                    value={selectedDocType}
                    onChange={(e) => setSelectedDocType(e.target.value)}
                    label="Document Type"
                  >
                    {documentTypes.map((type) => (
                      <MenuItem key={type.id} value={type.id}>
                        {type.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Button
                  variant="outlined"
                  component="label"
                  fullWidth
                  sx={{
                    mt: 2,
                    borderColor: '#FFC107',
                    color: '#000',
                    '&:hover': {
                      borderColor: '#FFD54F',
                      bgcolor: 'rgba(255, 193, 7, 0.1)',
                    },
                  }}
                >
                  Choose File
                  <input type="file" hidden accept="image/*,application/pdf" onChange={handleFileSelect} />
                </Button>

                {selectedFile && (
                  <Typography variant="body2" sx={{ mt: 1 }} color="text.secondary">
                    Selected: {selectedFile.name}
                  </Typography>
                )}

                <Button
                  variant="contained"
                  fullWidth
                  startIcon={<CloudUpload />}
                  onClick={handleUploadDocument}
                  disabled={loading || !selectedDocType || !selectedFile}
                  sx={{
                    mt: 2,
                    bgcolor: '#FFC107',
                    color: '#000',
                    '&:hover': {
                      bgcolor: '#FFD54F',
                    },
                  }}
                >
                  Upload Document
                </Button>
              </CardContent>
            </Card>
          )}
        </Grid>

        {/* Documents List */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Uploaded Documents
              </Typography>

              {!selectedDriver ? (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  Please select a driver to view their documents
                </Typography>
              ) : documents.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  No documents uploaded yet
                </Typography>
              ) : (
                <TableContainer component={Paper} sx={{ mt: 2 }}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Document Type</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Uploaded Date</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {documents.map((doc) => (
                        <TableRow key={doc.id}>
                          <TableCell>{doc.document_type_name || doc.document_type}</TableCell>
                          <TableCell>{getStatusChip(doc.verification_status)}</TableCell>
                          <TableCell>
                            {new Date(doc.created_at).toLocaleDateString()}
                          </TableCell>
                          <TableCell>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<Visibility />}
                              onClick={() => handleViewDocument(doc.document_url)}
                              sx={{ mr: 1 }}
                            >
                              View Document
                            </Button>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleOpenDeleteDialog(doc.id)}
                              title="Delete Document"
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Image Viewer Modal */}
      <Dialog
        open={openImageViewer}
        onClose={handleCloseImageViewer}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>Document Preview</DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: 400,
              bgcolor: '#f5f5f5',
              borderRadius: 1,
              p: 2
            }}
          >
            {viewImageUrl && (
              <img
                src={viewImageUrl}
                alt="Document"
                style={{
                  maxWidth: '100%',
                  maxHeight: '70vh',
                  objectFit: 'contain',
                  borderRadius: '4px'
                }}
                onError={(e) => {
                  console.error('Image failed to load:', viewImageUrl);
                  e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgZmlsbD0iI2VlZSIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LXNpemU9IjE4IiBmaWxsPSIjOTk5IiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBkeT0iLjNlbSI+SW1hZ2UgTm90IEZvdW5kPC90ZXh0Pjwvc3ZnPg==';
                  e.target.style.maxWidth = '200px';
                }}
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseImageViewer}
            variant="contained"
            sx={{
              bgcolor: '#FFC107',
              color: '#000',
              '&:hover': {
                bgcolor: '#FFD54F',
              },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={openDeleteDialog}
        onClose={handleCloseDeleteDialog}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this document? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} sx={{ color: '#000' }}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteDocument}
            variant="contained"
            sx={{
              bgcolor: '#FFC107',
              color: '#000',
              '&:hover': {
                bgcolor: '#FFD54F',
              },
            }}
            disabled={loading}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DriverDocumentsPage;
